'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/comps/primitives/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/comps/primitives/table';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { ModelMixPieSkeleton } from '@/domains/pages/model-savings/components/model-mix-pie';
import { REPORT_DATATABLE_TABLE_CLASSNAME } from '@/comps/table/constants';
const MODEL_MIX_COLUMN_WIDTH_PX = 200;
const TABLE_SKELETON_ROW_COUNT = 8;
function ScopeBarSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-nowrap items-center gap-2 border-b border-b-lightborder px-6 py-2"><Skeleton className="h-7 w-52 max-w-full rounded-md" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SavingsOpportunityCardSkeleton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-7 w-28 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="size-[136px] shrink-0 rounded-full" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AiProficiencyReportCard title="Model savings" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col justify-center !py-4" headerTrailing={t0}><div className="flex items-center gap-8">{t1}<div className="min-w-0 flex-1"><Skeleton className="h-9 w-44 max-w-full" /><Skeleton className="mt-2 h-4 w-56 max-w-full" /></div></div><Skeleton className="mt-4 h-3 w-full max-w-md" /></AiProficiencyReportCard>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function TopOpportunityRowSkeleton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="mt-0.5 size-6 shrink-0 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="min-w-0 flex-1"><Skeleton className="h-4 w-40 max-w-full" /><Skeleton className="mt-1 h-3 w-32 max-w-full" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex w-full items-start gap-3 px-2 py-2">{t0}{t1}<div className="flex shrink-0 flex-col items-end gap-1"><Skeleton className="h-4 w-16" /><Skeleton className="size-[18px] rounded-full" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function TopOpportunitiesCardSkeleton() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-3"><Skeleton className="hidden h-3 w-28 sm:block" /><Skeleton className="h-7 w-40 rounded-full" /></div>;
    t1 = <Skeleton className="mb-3 h-3 w-56 max-w-full" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AiProficiencyReportCard title="Top savings opportunities" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col !py-3" headerTrailing={t0}>{t1}<div className="flex flex-col gap-2">{Array.from({
          length: 3
        }).map(_temp)}</div></AiProficiencyReportCard>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(_, index) {
  return <TopOpportunityRowSkeleton key={index} />;
}
function SavingsTaskTableRowSkeleton() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TableCell className="pl-8"><Skeleton className="h-4 w-3/4 max-w-52" /></TableCell>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableCell><div className="flex items-center gap-2"><Skeleton className="h-4 w-16" /><Skeleton className="size-4 rounded-full" /></div></TableCell>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableCell><Skeleton className="h-4 w-20" /></TableCell>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TableCell style={{
      width: MODEL_MIX_COLUMN_WIDTH_PX,
      minWidth: MODEL_MIX_COLUMN_WIDTH_PX
    }}><ModelMixPieSkeleton /></TableCell>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableCell style={{
      width: MODEL_MIX_COLUMN_WIDTH_PX,
      minWidth: MODEL_MIX_COLUMN_WIDTH_PX
    }}><ModelMixPieSkeleton /></TableCell>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableRow>{t0}{t1}{t2}{t3}{t4}<TableCell className="pr-6 text-right"><Skeleton className="ml-auto h-7 w-20 rounded-full" /></TableCell></TableRow>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function SavingsTaskTableSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <colgroup><col /><col className="w-40" /><col className="w-40" /><col style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }} /><col style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }} /><col className="w-40" /></colgroup>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableHeader className="sticky top-0 z-0 bg-neutral-950"><TableRow className="border-t"><TableHead className="pl-8">Task</TableHead><TableHead>Saving / yr</TableHead><TableHead>Spend / yr</TableHead><TableHead>Current mix</TableHead><TableHead>Optimal mix</TableHead><TableHead className="pr-6 text-right"> </TableHead></TableRow></TableHeader>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="overflow-x-auto"><Table className={REPORT_DATATABLE_TABLE_CLASSNAME}>{t0}{t1}<TableBody striped={true}>{Array.from({
            length: TABLE_SKELETON_ROW_COUNT
          }).map(_temp2)}</TableBody></Table></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AiProficiencyReportCard title="Savings opportunity by task" description="Ranked by annualized savings if each task used the most cost-efficient model." contentClassName="flex min-h-0 flex-1 flex-col p-0!">{t2}<div className="flex h-14 items-center justify-end border-t border-t-lightborder bg-neutral-950 px-8 py-3"><Skeleton className="h-8 w-48" /></div></AiProficiencyReportCard>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp2(_, index) {
  return <SavingsTaskTableRowSkeleton key={index} />;
}
export function ModelSavingsChartsLoadingSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="col-span-2 flex border-r border-r-lightborder"><SavingsOpportunityCardSkeleton /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <><div className="grid grid-cols-5 items-stretch border-b border-b-lightborder">{t0}<div className="col-span-3 flex"><TopOpportunitiesCardSkeleton /></div></div><SavingsTaskTableSkeleton /></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ModelSavingsPageLoadingSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="border-b border-b-lightborder" data-testid="model-savings-page-loading"><ScopeBarSkeleton /><ModelSavingsChartsLoadingSkeleton /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}